import React, { useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ArrowLeft, Shield, Lock, Eye, Users, Cookie, Mail } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const lastUpdated = "December 2024";

  return (
    <div className="min-h-screen bg-black">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-black/95 backdrop-blur-sm border-b border-zinc-800">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              className="text-gray-400 hover:text-white"
              onClick={() => navigate('/')}
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Home
            </Button>
          </div>
          <div className="text-blue-500 font-bold text-xl">ShopNFCTags</div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-6 py-12 max-w-4xl">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <Shield className="w-16 h-16 mx-auto mb-6 text-blue-500" />
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Privacy Policy
          </h1>
          <p className="text-gray-400">
            Last Updated: {lastUpdated}
          </p>
        </div>

        {/* Introduction */}
        <section className="mb-12">
          <p className="text-gray-300 leading-relaxed mb-6">
            At ShopNFCTags ("we," "us," or "our"), we are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website and purchase our products.
          </p>
          <p className="text-gray-300 leading-relaxed">
            By using our website, you agree to the collection and use of information in accordance with this policy. If you do not agree with our policies and practices, please do not use our services.
          </p>
        </section>

        {/* Information We Collect */}
        <section className="mb-12">
          <div className="flex items-center gap-3 mb-6">
            <Eye className="w-8 h-8 text-blue-500" />
            <h2 className="text-2xl md:text-3xl font-bold text-white">
              Information We Collect
            </h2>
          </div>

          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-semibold text-white mb-3">
                Personal Information
              </h3>
              <p className="text-gray-400 leading-relaxed mb-3">
                We collect information that you provide directly to us, including:
              </p>
              <ul className="space-y-2 text-gray-400 ml-6">
                <li className="flex items-start gap-2">
                  <span className="text-blue-500 mt-1">•</span>
                  <span>Name, email address, phone number, and shipping address when you place an order</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-500 mt-1">•</span>
                  <span>Payment information (processed securely through our payment processor)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-500 mt-1">•</span>
                  <span>Communication preferences and any information you provide when contacting customer service</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-500 mt-1">•</span>
                  <span>Custom design specifications for personalized NFC products</span>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-white mb-3">
                Automatically Collected Information
              </h3>
              <p className="text-gray-400 leading-relaxed mb-3">
                When you visit our website, we automatically collect certain information, including:
              </p>
              <ul className="space-y-2 text-gray-400 ml-6">
                <li className="flex items-start gap-2">
                  <span className="text-blue-500 mt-1">•</span>
                  <span>IP address, browser type, and operating system</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-500 mt-1">•</span>
                  <span>Pages viewed, time spent on pages, and navigation paths</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-500 mt-1">•</span>
                  <span>Device information and unique identifiers</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-500 mt-1">•</span>
                  <span>Referral source and exit pages</span>
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* How We Use Your Information */}
        <section className="mb-12">
          <div className="flex items-center gap-3 mb-6">
            <Users className="w-8 h-8 text-cyan-500" />
            <h2 className="text-2xl md:text-3xl font-bold text-white">
              How We Use Your Information
            </h2>
          </div>

          <p className="text-gray-400 leading-relaxed mb-4">
            We use the information we collect for the following purposes:
          </p>
          <ul className="space-y-3 text-gray-400 ml-6">
            <li className="flex items-start gap-2">
              <span className="text-cyan-500 mt-1">•</span>
              <span><strong className="text-white">Order Processing:</strong> To process and fulfill your orders, including shipping and customer service</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-cyan-500 mt-1">•</span>
              <span><strong className="text-white">Communication:</strong> To send order confirmations, shipping updates, and respond to inquiries</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-cyan-500 mt-1">•</span>
              <span><strong className="text-white">Service Improvement:</strong> To understand how customers use our website and improve our products and services</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-cyan-500 mt-1">•</span>
              <span><strong className="text-white">Marketing:</strong> To send promotional emails about new products and special offers (you may opt out at any time)</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-cyan-500 mt-1">•</span>
              <span><strong className="text-white">Legal Compliance:</strong> To comply with applicable laws and protect our legal rights</span>
            </li>
          </ul>
        </section>

        {/* Information Sharing */}
        <section className="mb-12">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">
            Information Sharing and Disclosure
          </h2>
          <p className="text-gray-400 leading-relaxed mb-4">
            We do not sell, trade, or rent your personal information to third parties. We may share your information in the following circumstances:
          </p>
          <Card className="bg-zinc-900 border-zinc-800 mb-4">
            <CardContent className="p-6">
              <h3 className="text-lg font-semibold text-white mb-3">Service Providers</h3>
              <p className="text-gray-400 leading-relaxed">
                We may share information with trusted third-party service providers who assist us in operating our website, processing payments, shipping products, and conducting our business. These parties are obligated to keep your information confidential and use it only for the purposes we specify.
              </p>
            </CardContent>
          </Card>
          <Card className="bg-zinc-900 border-zinc-800 mb-4">
            <CardContent className="p-6">
              <h3 className="text-lg font-semibold text-white mb-3">Legal Requirements</h3>
              <p className="text-gray-400 leading-relaxed">
                We may disclose your information if required by law, court order, or governmental regulation, or if we believe disclosure is necessary to protect our rights, your safety, or the safety of others.
              </p>
            </CardContent>
          </Card>
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-6">
              <h3 className="text-lg font-semibold text-white mb-3">Business Transfers</h3>
              <p className="text-gray-400 leading-relaxed">
                In the event of a merger, acquisition, or sale of assets, your information may be transferred to the acquiring entity.
              </p>
            </CardContent>
          </Card>
        </section>

        {/* Cookies */}
        <section className="mb-12">
          <div className="flex items-center gap-3 mb-6">
            <Cookie className="w-8 h-8 text-orange-500" />
            <h2 className="text-2xl md:text-3xl font-bold text-white">
              Cookies and Tracking Technologies
            </h2>
          </div>
          <p className="text-gray-400 leading-relaxed mb-4">
            We use cookies and similar tracking technologies to enhance your browsing experience, analyze website traffic, and understand user behavior. Cookies are small data files stored on your device.
          </p>
          <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-6">
            <p className="text-gray-300 leading-relaxed mb-3">
              You can control cookies through your browser settings and choose to disable them. However, disabling cookies may limit your ability to use certain features of our website.
            </p>
            <p className="text-gray-400 text-sm">
              Types of cookies we use: Essential cookies (required for website functionality), Analytics cookies (to understand usage patterns), and Marketing cookies (for personalized advertising).
            </p>
          </div>
        </section>

        {/* Data Security */}
        <section className="mb-12">
          <div className="flex items-center gap-3 mb-6">
            <Lock className="w-8 h-8 text-green-500" />
            <h2 className="text-2xl md:text-3xl font-bold text-white">
              Data Security
            </h2>
          </div>
          <p className="text-gray-400 leading-relaxed mb-4">
            We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. These measures include:
          </p>
          <ul className="space-y-2 text-gray-400 ml-6 mb-4">
            <li className="flex items-start gap-2">
              <span className="text-green-500 mt-1">•</span>
              <span>Encryption of sensitive data during transmission (SSL/TLS)</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-green-500 mt-1">•</span>
              <span>Secure payment processing through PCI-compliant payment processors</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-green-500 mt-1">•</span>
              <span>Regular security assessments and updates</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-green-500 mt-1">•</span>
              <span>Limited access to personal information by authorized personnel only</span>
            </li>
          </ul>
          <p className="text-gray-400 text-sm italic">
            Please note that no method of transmission over the internet or electronic storage is 100% secure. While we strive to protect your information, we cannot guarantee absolute security.
          </p>
        </section>

        {/* Your Rights */}
        <section className="mb-12">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">
            Your Privacy Rights
          </h2>
          <p className="text-gray-400 leading-relaxed mb-4">
            Depending on your location, you may have certain rights regarding your personal information:
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            <Card className="bg-zinc-900 border-zinc-800">
              <CardContent className="p-5">
                <h4 className="font-semibold text-white mb-2">Access & Portability</h4>
                <p className="text-gray-400 text-sm">Request a copy of your personal data we hold</p>
              </CardContent>
            </Card>
            <Card className="bg-zinc-900 border-zinc-800">
              <CardContent className="p-5">
                <h4 className="font-semibold text-white mb-2">Correction</h4>
                <p className="text-gray-400 text-sm">Request correction of inaccurate information</p>
              </CardContent>
            </Card>
            <Card className="bg-zinc-900 border-zinc-800">
              <CardContent className="p-5">
                <h4 className="font-semibold text-white mb-2">Deletion</h4>
                <p className="text-gray-400 text-sm">Request deletion of your personal data</p>
              </CardContent>
            </Card>
            <Card className="bg-zinc-900 border-zinc-800">
              <CardContent className="p-5">
                <h4 className="font-semibold text-white mb-2">Opt-Out</h4>
                <p className="text-gray-400 text-sm">Unsubscribe from marketing communications</p>
              </CardContent>
            </Card>
          </div>
          <p className="text-gray-400 mt-6 leading-relaxed">
            To exercise any of these rights, please contact us at{' '}
            <a href="mailto:privacy@shopnfctags.com" className="text-blue-500 hover:text-blue-400 transition-colors">
              privacy@shopnfctags.com
            </a>
          </p>
        </section>

        {/* Children's Privacy */}
        <section className="mb-12">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">
            Children's Privacy
          </h2>
          <p className="text-gray-400 leading-relaxed">
            Our services are not directed to children under the age of 13. We do not knowingly collect personal information from children under 13. If you are a parent or guardian and believe your child has provided us with personal information, please contact us immediately, and we will take steps to delete such information.
          </p>
        </section>

        {/* International Data Transfers */}
        <section className="mb-12">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">
            International Data Transfers
          </h2>
          <p className="text-gray-400 leading-relaxed">
            Your information may be transferred to and maintained on servers located outside of your state, province, country, or other governmental jurisdiction where data protection laws may differ. By using our services, you consent to the transfer of your information to the United States and other countries where we operate.
          </p>
        </section>

        {/* Changes to Privacy Policy */}
        <section className="mb-12">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">
            Changes to This Privacy Policy
          </h2>
          <p className="text-gray-400 leading-relaxed">
            We may update this Privacy Policy from time to time to reflect changes in our practices or for legal, operational, or regulatory reasons. We will notify you of any material changes by posting the updated policy on this page and updating the "Last Updated" date. We encourage you to review this policy periodically.
          </p>
        </section>

        {/* Contact Section */}
        <section className="bg-gradient-to-r from-blue-600 to-cyan-600 rounded-lg p-8 text-center">
          <Mail className="w-12 h-12 mx-auto mb-4 text-white" />
          <h3 className="text-2xl font-bold text-white mb-3">
            Questions About Privacy?
          </h3>
          <p className="text-white/90 mb-6">
            If you have questions or concerns about this Privacy Policy or our data practices, please contact us:
          </p>
          <div className="space-y-2 text-white mb-6">
            <p className="font-semibold">ShopNFCTags Privacy Team</p>
            <p>Email: <a href="mailto:privacy@shopnfctags.com" className="underline hover:text-gray-100">privacy@shopnfctags.com</a></p>
            <p>General Inquiries: <a href="mailto:orders@shopnfctags.com" className="underline hover:text-gray-100">orders@shopnfctags.com</a></p>
          </div>
          <Button
            className="bg-white text-blue-600 hover:bg-gray-100 font-semibold"
            onClick={() => window.location.href = 'mailto:privacy@shopnfctags.com'}
          >
            Contact Privacy Team
          </Button>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-zinc-950 border-t border-zinc-800 py-8 mt-16">
        <div className="container mx-auto px-6 text-center">
          <p className="text-gray-500 text-sm">
            © 2026 ShopNFCTags. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
